import React from 'react';
import { Helmet as ReactHelmet } from 'react-helmet';
import { withTheme } from '@emotion/react';
import { useNavigationQuery } from '@queries/useNavigationQuery';
import { createLanguagePath, getDomainForLocale } from '@scripts/utils/domain';
import { getSrc } from 'gatsby-plugin-image';

export type HelmetProps = {
  theme: any;
  description?: string;
  title?: string;
  langObj: any;
  locale: string;
  image?: any;
  children?: any;
  matomo?: string;
};

const Helmet = ({
  matomo,
  langObj,
  locale,
  description,
  title,
  image,
  children,
}: HelmetProps) => {
  const { nodes } = useNavigationQuery();

  const langNode = nodes.filter(
    (node: { node_locale: string }) => node.node_locale == locale,
  )[0];

  const navTitle = title || langNode.campaignName || '';
  const navDescription = description || langNode.teaser || '';
  const navImage = image?.image?.openGraphImage
    ? getSrc(image.image.openGraphImage)
    : langNode.defaultPicture?.gatsbyImageData
    ? getSrc(langNode.defaultPicture.gatsbyImageData)
    : '';
  const url = langObj?.switcher
    ? createLanguagePath({
        switcher: langObj.switcher,
        language: locale,
      })
    : getDomainForLocale(locale);

  return (
    <ReactHelmet
      htmlAttributes={{ lang: locale }}
      onChangeClientState={() => null} // adding 'onChangeClientState' prop fixes missing htmlAttributes on first render on develop
    >
      <title>{navTitle}</title>

      {langObj &&
        langObj.switcher.map(
          (langItem: any) =>
            langItem.node_locale === locale && (
              <link
                key={`${langItem.node_locale}-canonical-key`}
                rel="canonical"
                href={createLanguagePath({
                  switcher: langObj.switcher ? langObj.switcher : [],
                  language: langItem.node_locale,
                })}
              />
            ),
        )}

      {langObj &&
        langObj.switcher.map((langItem: any) => (
          <link
            key={`${langItem.node_locale}-hreflang-key`}
            rel="alternate"
            hrefLang={langItem.node_locale}
            href={createLanguagePath({
              switcher: langObj.switcher ? langObj.switcher : [],
              language: langItem.node_locale,
            })}
          />
        ))}

      <meta name="description" content={navDescription} />
      <meta property="og:title" content={navTitle} />
      <meta property="og:description" content={navDescription} />
      <meta property="og:image" content={navImage} />
      <meta
        property="og:site_name"
        content={langNode.campaignName || navTitle}
      />
      <meta property="og:locale" content={locale} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={navTitle} />
      <meta name="twitter:description" content={navDescription} />
      <meta name="twitter:image" content={navImage} />
      <meta name="twitter:image:src" content={navImage} />

      <script type="text/javascript">
        {`
          var mousePosX = 0;
          var mousePosY = 0;

          (function () {
            const html = document.documentElement;
            const fontsClass = 'fonts-loaded';

            if (sessionStorage.fontsLoaded) {
              html
                .classList
                .add(fontsClass);
            } else {
              const script = document.createElement("script");
              script.src = "/vendors/fontfaceobserver-2.1.0.js";
              script.async = true;
              script.onload = function () {
                const fontFamilyA = 'Scto Grotesk A';

                const fontA100 = new FontFaceObserver(fontFamilyA, {weight: '100'});
                const fontA300 = new FontFaceObserver(fontFamilyA, {weight: '300'});
                const fontA400 = new FontFaceObserver(fontFamilyA, {weight: '400'});
                const fontA500 = new FontFaceObserver(fontFamilyA, {weight: '500'});
                const fontA700 = new FontFaceObserver(fontFamilyA, {weight: '700'});
                const fontA900 = new FontFaceObserver(fontFamilyA, {weight: '900'});

                Promise
                  .all([fontA100.load(), fontA300.load(), fontA400.load(), fontA500.load(), fontA700.load(), fontA900.load()])
                  .then(function () {
                    html
                      .classList
                      .add(fontsClass);
                    sessionStorage.fontsLoaded = true;
                  });
              };
              document
                .head
                .appendChild(script);
            }

            var getMousePos = function(e) {
              mousePosX = e.clientX;
              mousePosY = e.clientY;
            };

            document.addEventListener('mousemove', getMousePos);
          })();
        `}
      </script>

      <script type="text/javascript">
        {`
          window.onload = function() {
            var isIE = navigator
              ? navigator.userAgent.indexOf('MSIE') !== -1 ||
                navigator.appVersion.indexOf('Trident/') > -1
              : false;

            var text = {
              de:
                'Diese Website kann im Internet Explorer nicht angezeigt werden. Bitte wechseln Sie auf einen neuen Browser, um sich die Website anzusehen.',
              fr:
                'Ce site web ne peut pas être affiché dans Internet Explorer. Veuillez passer à un nouveau browser pour visualiser le site.',
              it:
                'Questo sito non può essere visualizzato in Internet Explorer. Si prega di passare a un nuovo browser per visualizzare il sito web.',
            };

            if (isIE) {
              var updateBrowserDiv = document.createElement('div');
              var lang = document.documentElement.lang;

              updateBrowserDiv.textContent = text[lang] ? text[lang] : text.de;
              updateBrowserDiv.style.position = 'fixed';
              updateBrowserDiv.style.bottom = '0';
              updateBrowserDiv.style.left = '0';
              updateBrowserDiv.style.right = '0';
              updateBrowserDiv.style.padding = '40px 20px';
              updateBrowserDiv.style.backgroundColor = '#E53E3E';
              updateBrowserDiv.style.color = '#ffffff';
              updateBrowserDiv.style.fontSize = '18px';
              updateBrowserDiv.style.zIndex = '100';

              document.body.appendChild(updateBrowserDiv);
            }
          }
        `}
      </script>
      {matomo ? (
        <script type="text/javascript">
          {`
            var _mtm = window._mtm = window._mtm || [];
            _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
            var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
            g.async=true; g.src='https://data.energieschweiz.ch/js/container_${matomo}.js'; s.parentNode.insertBefore(g,s);
          `}
        </script>
      ) : null}
      {children}
    </ReactHelmet>
  );
};

export default withTheme(Helmet);
